/**
 * Règles d'AFFICHAGE des compteurs d'une machine : ce qui se propose par défaut, ce qui se montre.
 *
 * Le serveur reste l'autorité : il refuse de désigner un compteur qui recule ou qui n'est pas en heures
 * et le dit en français. L'écran ne recopie pas ces règles, il propose seulement.
 */
import type { Releve } from '../api/client';

/**
 * Un compteur dont le libellé dit « heures totales » est, dans l'immense majorité des cas, celui qui
 * commande les entretiens : la case « Compteur des entretiens » est cochée d'emblée (jamais imposée).
 * Insensible aux majuscules, aux accents et aux espaces multiples.
 */
export function proposeCommeCompteurDesEntretiens(libelle: string): boolean {
  const normalise = libelle
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  return /\bheures? totales?\b/.test(normalise);
}

/**
 * Les relevés qu'un autre relevé a déjà rectifiés. Un relevé rectifié reste lisible (rien ne s'efface)
 * mais ne se rectifie plus : c'est son remplaçant qu'il faut corriger, sinon deux relevés rivaliseraient
 * pour la même place.
 */
export function relevesDejaRectifies(releves: readonly Releve[]): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const r of releves) if (r.correctsReadingId) ids.add(r.correctsReadingId);
  return ids;
}

/**
 * La date que propose le « Relevé du jour » une fois l'enregistrement fait. Quand tout est enregistré, on
 * repart d'AUJOURD'HUI : sinon la date tapée pour un relevé rétroactif (28/09/2026, par exemple) resterait
 * dans le formulaire et daterait à tort le relevé suivant, sans que rien ne le signale. Quand un compteur
 * a échoué, la date saisie est CONSERVÉE : sa ligne reste à renvoyer, et la renvoyer sous une autre date
 * changerait sa date sans prévenir.
 */
export function dateApresReleves(dateSaisie: string, aujourdhui: string, nombreEchecs: number): string {
  return nombreEchecs === 0 ? aujourdhui : dateSaisie;
}

/**
 * Parmi des lignes de création de compteurs, laquelle devient LE compteur des entretiens : la première
 * cochée. Il n'y en a qu'un par machine ; les suivantes, cochées par erreur, ne sont pas retenues.
 */
export function premiereLigneDesignee<T extends { readonly id: number }>(
  lignes: readonly T[],
  estDesignee: (ligne: T) => boolean,
): number | null {
  const ligne = lignes.find(estDesignee);
  return ligne ? ligne.id : null;
}
